import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { chaaiAssistantService } from '@/services/chaaiAssistantService'
import { X, Send, Loader2, RotateCcw, Bot } from 'lucide-react'

type ChatRole = 'user' | 'assistant'

interface ChatMessage {
  id: string
  role: ChatRole
  content: string
  solicitationTypeOptions?: Array<{
    id: 'automation' | 'maintenance' | 'improvement_update'
    label: string
    description: string
    prompt: string
  }>
}

const renderMessageContent = (content: string): React.ReactNode[] => {
  const normalized = content.replace(/\r\n/g, '\n')
  const parts = normalized.split(/(\*\*[^*\n]+?\*\*)/g)

  return parts.map((part, index) => {
    const boldMatch = part.match(/^\*\*([^*\n]+?)\*\*$/)
    if (boldMatch) {
      return (
        <strong key={`b-${index}`} className="font-semibold">
          {boldMatch[1]}
        </strong>
      )
    }

    return <React.Fragment key={`t-${index}`}>{part}</React.Fragment>
  })
}

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content:
    'Olá, sou o Chaai Assistant. Posso tirar dúvidas sobre o Hub, indicar onde encontrar funcionalidades e avisar se já existe alguma melhoria parecida cadastrada. Em que posso ajudar?',
}

export const ChaaiAssistantWidget: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE])
  const listRef = useRef<HTMLDivElement>(null)
  const sessionIdRef = useRef<string | null>(null)

  const hideOnRoute =
    location.pathname === '/login' ||
    location.pathname === '/first-access' ||
    location.pathname === '/forgot-password' ||
    location.pathname.startsWith('/auth/')
  const allowedRoutes = ['/dashboard', '/automacoes', '/fila-melhorias']
  const isAllowedRoute = allowedRoutes.some((route) => location.pathname.startsWith(route))

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [messages, open])

  useEffect(() => {
    const openChaai = () => setOpen(true)
    window.addEventListener('chaai:open', openChaai)
    return () => window.removeEventListener('chaai:open', openChaai)
  }, [])

  const buildSessionId = useCallback((): string => {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return `chaai-${crypto.randomUUID()}`
    }
    return `chaai-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  }, [])

  const ensureSessionId = useCallback((): string => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = buildSessionId()
    }
    return sessionIdRef.current
  }, [buildSessionId])

  const consumeSessionId = useCallback((): string | null => {
    const currentSessionId = sessionIdRef.current
    sessionIdRef.current = null
    return currentSessionId
  }, [])

  const endCurrentSessionKeepalive = useCallback(() => {
    const currentSessionId = consumeSessionId()
    if (!currentSessionId) return
    chaaiAssistantService.endSessionKeepalive(currentSessionId)
  }, [consumeSessionId])

  const startNewConversation = useCallback(async () => {
    const currentSessionId = consumeSessionId()
    if (currentSessionId) {
      try {
        await chaaiAssistantService.endSession(currentSessionId)
      } catch {
        // Não bloqueia UX se falhar.
      }
    }
    setMessages([{ ...WELCOME_MESSAGE }])
    setInput('')
  }, [consumeSessionId])

  useEffect(() => {
    const handlePageHide = () => {
      endCurrentSessionKeepalive()
    }

    const handleBeforeUnload = () => {
      endCurrentSessionKeepalive()
    }

    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [endCurrentSessionKeepalive])

  const send = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? input).trim()
    if (!text || sending) return

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: text,
    }
    const nextMessages: ChatMessage[] = [...messages, userMsg]
    setMessages(nextMessages)
    setInput('')
    setSending(true)

    const conversation = nextMessages
      .slice(-40)
      .map(({ role, content }) => ({ role, content }))

    try {
      const sessionId = ensureSessionId()
      const { reply, solicitationTypeOptions } = await chaaiAssistantService.sendMessage({
        message: text,
        conversation,
        sessionId,
      })
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: reply,
          solicitationTypeOptions,
        },
      ])
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message: string }).message)
          : 'Não foi possível obter resposta. Tente novamente.'
      setMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          role: 'assistant',
          content: msg,
        },
      ])
    } finally {
      setSending(false)
    }
  }, [ensureSessionId, input, sending, messages])

  const handleToggleOpen = useCallback(() => {
    setOpen((previous) => !previous)
  }, [])

  if (isLoading || !isAuthenticated || !user || hideOnRoute || !isAllowedRoute) {
    return null
  }

  return (
    <>
      <button
        type="button"
        aria-label={open ? 'Fechar Chaai Assistant' : 'Abrir Chaai Assistant'}
        aria-expanded={open}
        onClick={handleToggleOpen}
        className={`fixed bottom-8 right-8 z-[9998] flex h-16 w-16 rounded-full shadow-lg transition focus:outline-none focus:ring-2 focus:ring-neutral-400 focus:ring-offset-2 focus:ring-offset-neutral-100 ${
          open
            ? 'items-center justify-center bg-neutral-700 text-white ring-[3px] ring-white hover:bg-neutral-600'
            : 'animate-chaai-pulse items-center justify-center ring-[3px] ring-neutral-600 bg-surface text-body'
        }`}
      >
        {open ? (
          <X className="h-8 w-8 shrink-0" aria-hidden />
        ) : (
          <Bot className="h-8 w-8 shrink-0" aria-hidden />
        )}
      </button>

      {open && (
        <div
          className="fixed bottom-[6.5rem] right-8 z-[9998] flex max-h-[min(520px,75vh)] w-[min(100vw-2.5rem,24rem)] flex-col overflow-hidden rounded-[5px] border border-border-subtle bg-surface shadow-2xl"
          role="dialog"
          aria-label="Chaai Assistant"
        >
          <div className="border-b border-border-subtle bg-neutral-800 px-4 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold text-white">Chaai Assistant</h2>
                <p className="text-xs text-neutral-300">Dúvidas sobre o Hub e melhorias</p>
              </div>
              <button
                type="button"
                onClick={() => void startNewConversation()}
                disabled={sending}
                title="Nova conversa (encerra a sessão atual no histórico)"
                className="shrink-0 rounded-[5px] border border-neutral-600 bg-neutral-700 p-1.5 text-white transition hover:bg-neutral-600 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Nova conversa"
              >
                <RotateCcw className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>

          <div
            ref={listRef}
            className="flex-1 space-y-3 overflow-y-auto bg-surface-raised px-3 py-3"
            style={{ maxHeight: 'min(380px, 50vh)' }}
          >
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[90%] rounded-lg border px-3 py-2 text-sm ${
                    m.role === 'user'
                      ? 'border-border-strong bg-surface-raised text-body shadow-sm'
                      : 'border-border-subtle bg-surface text-body shadow-sm'
                  }`}
                >
                  {m.content.trim() ? (
                    <p className="whitespace-pre-wrap break-words">{renderMessageContent(m.content)}</p>
                  ) : null}
                  {m.role === 'assistant' && m.solicitationTypeOptions?.length ? (
                    <div className="mt-2 space-y-2">
                      {m.solicitationTypeOptions.map((option) => (
                        <button
                          key={`${m.id}-${option.id}`}
                          type="button"
                          onClick={() => void send(option.prompt)}
                          disabled={sending}
                          className="w-full rounded-[5px] border border-neutral-300 dark:border-neutral-700 bg-surface px-2 py-2 text-left text-neutral-900 dark:text-neutral-200 transition hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <div className="text-xs font-semibold">{option.label}</div>
                          <div className="text-xs opacity-90">{option.description}</div>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-lg border border-border-subtle bg-surface px-3 py-2 text-sm text-muted">
                  <Loader2 className="h-4 w-4 animate-spin text-muted" />
                  Pensando…
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-border-subtle bg-surface-muted p-2">
            <div className="flex gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    void send()
                  }
                }}
                placeholder="Digite sua pergunta…"
                rows={2}
                className="min-h-[44px] flex-1 resize-none rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm text-body placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-2 focus:ring-neutral-400/50"
                disabled={sending}
              />
              <button
                type="button"
                onClick={() => void send()}
                disabled={sending || !input.trim()}
                className="self-end rounded-lg border border-neutral-600 bg-neutral-700 p-2 text-white transition hover:bg-neutral-600 disabled:opacity-50"
                aria-label="Enviar"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
